import { useEffect, useState } from "react";
import { Send, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";
import { Chips } from "./Chips";

function NavRelease({ acc, catalogue, onSaved }) {
  const [keys, setKeys] = useState(acc.released_nav || []);
  useEffect(() => setKeys(acc.released_nav || []), [acc.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try { await api.put(`/features/release/${acc.id}`, { nav: keys }); toast.success(`Sidebar released to ${acc.name}`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-4 space-y-3" data-testid="release-nav">
      <div className="text-sm font-semibold text-slate-100">Sidebar sections released to {acc.name}</div>
      <p className="text-xs text-[#EBD6A0]/80">Click a section to tick it (✓), then click Save release.</p>
      <Chips testid="release-nav" items={catalogue.map((c) => [c.key, c.label])} value={keys} onChange={setKeys} />
      <p className="text-[11px] text-slate-500">The entity's owner then chooses who sees each section in their Features Builder → Sidebar access.</p>
      <div className="flex justify-end"><Button size="sm" onClick={save} data-testid="release-nav-save">Save release</Button></div>
    </div>
  );
}

function DashRelease({ acc, dashboards }) {
  const [done, reload] = useGet(`/features/released-dashboards/${acc.id}`, [acc.id]);
  const send = async (d) => {
    try { const r = await api.post("/features/release-dashboard", { dashboard_id: d.id, account_id: acc.id }); toast.success(r.data.updated ? `Updated "${d.name}" in ${acc.name}` : `Released "${d.name}" to ${acc.name}`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-4 space-y-2" data-testid="release-dashboards">
      <div className="text-sm font-semibold text-slate-100">Dashboards</div>
      {!dashboards.length && <div className="text-xs text-slate-500">Build a dashboard in the Dashboards tab first.</div>}
      {dashboards.map((d) => {
        const sent = (done || []).includes(d.id);
        return (
          <div key={d.id} className="flex items-center justify-between gap-3 border-b border-white/5 py-2" data-testid={`release-dash-${d.id}`}>
            <div className="text-sm text-slate-200">{d.name} <span className="text-xs text-slate-500">· {d.widgets.length} widgets</span>{sent && <span className="ml-2 text-[11px] text-emerald-400 inline-flex items-center gap-1"><Check className="h-3 w-3" />released</span>}</div>
            <Button size="sm" variant="outline" onClick={() => send(d)} data-testid={`release-dash-btn-${d.id}`}><Send className="h-3.5 w-3.5 mr-1" />{sent ? "Send update" : "Release"}</Button>
          </div>
        );
      })}
    </div>
  );
}

export function ReleaseTab({ dashboards }) {
  const { accounts, reloadAccounts } = useAuth();
  const [catalogue] = useGet("/features/catalogue");
  const targets = (accounts || []).filter((a) => !a.is_lab && a.status !== "cancelled");
  const [aid, setAid] = useState("");
  const acc = targets.find((a) => a.id === aid);
  return (
    <div className="rounded-xl bg-black p-6 space-y-4 min-h-[40vh]" data-testid="release-tab">
      <p className="text-sm text-slate-400">Release finished dashboards and sidebar sections from the Development Lab to an Entity Account.</p>
      <select className="h-9 w-full max-w-sm rounded-md border border-input bg-background px-2 text-sm" value={aid} onChange={(e) => setAid(e.target.value)} data-testid="release-entity-select">
        <option value="">Choose an Entity Account…</option>
        {targets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      {acc && catalogue && <NavRelease acc={acc} catalogue={catalogue} onSaved={reloadAccounts} />}
      {acc && <DashRelease acc={acc} dashboards={dashboards || []} />}
    </div>
  );
}
